"use client";

import type React from "react";

import { useEffect, useRef, useCallback, useMemo, type FormEvent } from "react";
import { useState } from "react";
import { Textarea } from "../ui/textarea";
import { cn } from "../lib/utils";
import {
  MonitorIcon,
  ArrowUpIcon,
  Paperclip,
  PlusIcon,
  Loader2,
  ChevronDown,
  WrenchIcon,
  RefreshCw,
  FileText,
  X,
  FileBoxIcon,
  BoxIcon,
  RectangleHorizontalIcon,
} from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { MCPTool, MCPToolListItem } from "../lib/types/mcp";
import { TextShimmer } from "./text-shimmer";

interface UseAutoResizeTextareaProps {
  minHeight: number;
  maxHeight?: number;
}

function useAutoResizeTextarea({
  minHeight,
  maxHeight,
}: UseAutoResizeTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = useCallback(
    (reset?: boolean) => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      if (reset) {
        textarea.style.height = `${minHeight}px`;
        return;
      }

      // Temporarily shrink to get the right scrollHeight
      textarea.style.height = `${minHeight}px`;

      // Calculate new height
      const newHeight = Math.max(
        minHeight,
        Math.min(textarea.scrollHeight, maxHeight ?? Number.POSITIVE_INFINITY),
      );

      textarea.style.height = `${newHeight}px`;
    },
    [minHeight, maxHeight],
  );

  useEffect(() => {
    // Set initial height
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = `${minHeight}px`;
    }
  }, [minHeight]);

  // Adjust height on window resize
  useEffect(() => {
    const handleResize = () => adjustHeight();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [adjustHeight]);

  return { textareaRef, adjustHeight };
}

interface MCPServerInfo {
  id: string;
  name: string;
  url: string;
  transportType?: string;
  config?: any;
  tools: MCPToolExtended[];
  isLoading: boolean;
}

// Extended properties for MCP tools
interface MCPToolExtended extends MCPToolListItem {
  serverUrl?: string; // URL of the MCP server this tool belongs to
  serverId?: string; // ID of the MCP server this tool belongs to
  config?: any; // Config of the MCP server this tool belongs to
}

// Add KnowledgeSource type
interface KnowledgeSource {
  id: string;
  name: string;
  description: string | null;
  file_name: string;
  file_size: number;
  file_type: string;
  status: string;
}

interface KnowledgeScope {
  id: string;
  name: string;
  description: string | null;
  scope_knowledge_sources?: any[];
}

interface VercelV0ChatProps {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
  onStop?: () => void;
  loading?: boolean;
  placeholder?: string;
  showIngestForm?: boolean;
  showIntermediateStepsToggle?: boolean;
  intermediateSteps?: boolean;
  setIntermediateSteps?: (value: boolean) => void;
  showSuggestions?: boolean;
  setShowSuggestions?: (value: boolean) => void;
  onMCPToolSelect?: (tool: MCPToolExtended | null) => void;
  selectedMCPTool?: MCPToolExtended | null;
  renderExtraButton?: (() => React.ReactNode) | null;
  onKnowledgeSourcesSelect?: (sourceIds: string[]) => void;
  selectedKnowledgeSources?: string[];
  // Authentication context props
  isAuthenticated?: boolean;
  organizationId?: string;
  publicScope?: boolean;
  skipInitialization?: boolean;
  // Base URL for API calls
  baseUrl?: string;
  // Styling
  inputClassName?: string;
  buttonsClassName?: string;
  dropdownClassName?: string;
  dropdownDirection?: 'top' | 'bottom';
}

export function VercelV0Chat({
  value,
  onChange,
  onSubmit,
  onStop,
  loading = false,
  placeholder = "Ask Zynex Agent a question...",
  showIngestForm,
  showIntermediateStepsToggle,
  intermediateSteps,
  setIntermediateSteps,
  showSuggestions,
  setShowSuggestions,
  onMCPToolSelect,
  selectedMCPTool,
  renderExtraButton,
  onKnowledgeSourcesSelect,
  selectedKnowledgeSources = [],
  isAuthenticated,
  organizationId,
  publicScope,
  skipInitialization,
  baseUrl = "https://zynextechai.com",
  inputClassName,
  buttonsClassName,
  dropdownClassName,
  dropdownDirection = 'bottom',
}: VercelV0ChatProps = {}) {
  const [localValue, setLocalValue] = useState("");
  const { textareaRef, adjustHeight } = useAutoResizeTextarea({
    minHeight: 60,
    maxHeight: 200,
  });
  const [mcpServers, setMcpServers] = useState<MCPServerInfo[]>([]);
  const [isLoadingServers, setIsLoadingServers] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mcpToolsCache, setMcpToolsCache] = useState<Map<string, MCPToolExtended[]>>(new Map());
  const [lastFetchTime, setLastFetchTime] = useState<Map<string, number>>(new Map());
  const [knowledgeSources, setKnowledgeSources] = useState<KnowledgeSource[]>([]);
  const [isLoadingKnowledge, setIsLoadingKnowledge] = useState(false);
  const [knowledgeDropdownOpen, setKnowledgeDropdownOpen] = useState(false);
  const [knowledgeScopes, setKnowledgeScopes] = useState<KnowledgeScope[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedScope, setSelectedScope] = useState<string>("");
  const [scopedKnowledgeSources, setScopedKnowledgeSources] = useState<KnowledgeSource[]>([]);
  const [selectedMCPTools, setSelectedMCPTools] = useState<string[]>([]);
  const [collapsedServers, setCollapsedServers] = useState<Set<string>>(new Set());
  const [hasInitialized, setHasInitialized] = useState(false);
  const initializationRef = useRef(false);

  // Use either controlled or uncontrolled value
  const inputValue = value !== undefined ? value : localValue;



  // Helper function to build API URLs
  const buildApiUrl = useCallback((path: string) => {
    // Remove leading slash if present to avoid double slashes
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    return `${baseUrl}/${cleanPath}`;
  }, [baseUrl]);

  // Helper function to format tool names
  const formatToolName = (toolName: string) => {
    // Convert snake_case or camelCase to Title Case
    return toolName
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Handle camelCase
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize first letter of each word
  };

  useEffect(() => {
    if (skipInitialization || initializationRef.current) return; // ✅ FIXED GUARD

    const shouldFetch = isAuthenticated || (publicScope && organizationId);
    if (!shouldFetch) return;

    console.log('VercelV0Chat: Initializing resources...');
    initializationRef.current = true;
    setHasInitialized(true);
    
    // Call the functions directly instead of through dependencies
    (async () => {
      try {
        setIsLoadingServers(true);
        console.log("Fetching MCP servers...");
        
        // Build URL with parameters
        const params = new URLSearchParams();
        if (organizationId) {
          params.append('organizationId', organizationId);
        }
        
        const url = buildApiUrl(`/api/mcp/servers${params.toString() ? `?${params.toString()}` : ''}`);
        const response = await fetch(url);
        
        if (response.ok) {
          const data = await response.json();
          console.log("MCP servers response:", data);

          // Initialize servers without tools yet
          const serverList = data.servers.map((server: any) => ({
            id: server.id,
            name: server.name,
            url: server.url,
            transportType: server.transportType,
            config: server.config,
            tools: [] as MCPToolExtended[],
            isLoading: false,
          }));

          console.log("Loaded MCP servers:", serverList.length);
          setMcpServers(serverList);

          // Load tools for each server
          for (const server of serverList) {
            fetchToolsForServer(server.id);
          }
        } else {
          console.error("Failed to fetch MCP servers:", await response.text());
        }
      } catch (error) {
        console.error("Error fetching MCP servers:", error);
      } finally {
        setIsLoadingServers(false);
      }
    })();
    
    (async () => {
      try {
        setIsLoadingKnowledge(true);
        
        // Build URL with parameters
        const params = new URLSearchParams();
        if (organizationId) {
          params.append('organizationId', organizationId);
        }
        
        const url = buildApiUrl(`/api/knowledge/user-accessible${params.toString() ? `?${params.toString()}` : ''}`);
        const response = await fetch(url);
        
        if (response.ok) {
          const data = await response.json();
          // Filter only completed sources
          const completedSources = data.sources.filter(
            (source: KnowledgeSource) => source.status === "COMPLETED",
          );
          setKnowledgeSources(completedSources || []);
          // Set the scopes and admin status from the API response
          setKnowledgeScopes(data.scopes || []);
          setIsAdmin(data.isAdmin || false);
        } else {
          console.error(
            "Failed to fetch knowledge sources:",
            await response.text(),
          );
        }
      } catch (error) {
        console.error("Error fetching knowledge sources:", error);
      } finally {
        setIsLoadingKnowledge(false);
      }
    })();
  }, [isAuthenticated, publicScope, organizationId, skipInitialization]); // Added skipInitialization to dependencies

  // Update scoped sources when scope selection changes
  useEffect(() => {
    if (isAdmin && selectedScope) {
      const scope = knowledgeScopes.find(s => s.id === selectedScope);
      if (scope?.scope_knowledge_sources) {
        const scopeSources = scope.scope_knowledge_sources
          .map((sks: any) => sks.knowledge_sources)
          .filter(Boolean);
        setScopedKnowledgeSources(scopeSources);
      } else {
        setScopedKnowledgeSources([]);
      }
    } else if (isAdmin && !selectedScope) {
      // Admin with no scope selected sees all sources
      setScopedKnowledgeSources(knowledgeSources);
    } else {
      // Regular user sees their accessible sources
      setScopedKnowledgeSources(knowledgeSources);
    }
  }, [selectedScope, knowledgeScopes, knowledgeSources, isAdmin]);

  // Modify the function to fetch tools for a server
  const fetchToolsForServer = async (serverId: string, forceRefresh = false) => {
    // Find the server in our list
    const serverIndex = mcpServers.findIndex((s) => s.id === serverId);
    if (serverIndex === -1) {
      console.log(`Server ${serverId} not found in mcpServers array`);
      return;
    }

    const server = mcpServers[serverIndex];
    
    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cachedTools = mcpToolsCache.get(serverId);
      const lastFetch = lastFetchTime.get(serverId);
      const cacheAge = lastFetch ? Date.now() - lastFetch : Infinity;
      
      // Use cache if tools exist and cache is less than 5 minutes old
      if (cachedTools && cachedTools.length > 0 && cacheAge < 5 * 60 * 1000) {
        console.log(`Using cached tools for server ${serverId}`);
        setMcpServers((prev) => {
          const updated = [...prev];
          updated[serverIndex] = { ...updated[serverIndex], tools: cachedTools, isLoading: false };
          return updated;
        });
        return;
      }
    }

    // Mark server as loading
    setMcpServers((prev) => {
      const updated = [...prev];
      updated[serverIndex] = { ...updated[serverIndex], isLoading: true };
      return updated;
    });

    try {
      console.log(
        `Fetching tools for server ${serverId} (type: ${server.transportType})`,
      );

      if (server.transportType === 'config' && server.config) {
        // Use the new server-side API for config-based servers
        console.log(`Using server-side API for config-based server ${serverId}`);
        console.log(`Server config:`, server.config);
        const response = await fetch(buildApiUrl("/api/mcp/load-tools"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            serverId: serverId,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          console.log(`Received tools data:`, data);

          const toolsList = data.tools || [];
          const processedTools = toolsList.map(
            (tool: any) =>
              ({
                ...tool, // Keep ALL original properties
                id: `${serverId}_${tool.name}`,
                mcpServerName: server.name,
                serverId: serverId,
                serverUrl: server.url,
                config: server.config, // Add the server config to each tool
              }) as MCPToolExtended,
          );

          // Update cache
          setMcpToolsCache(prev => new Map(prev.set(serverId, processedTools)));
          setLastFetchTime(prev => new Map(prev.set(serverId, Date.now())));

          // Update the server with its tools
          setMcpServers((prev) => {
            console.log(
              `Updating server ${serverId} with ${toolsList.length} tools`,
            );

            const updated = [...prev];
            updated[serverIndex] = {
              ...updated[serverIndex],
              tools: processedTools,
              isLoading: false,
            };

            console.log(
              `Server ${serverId} now has tools:`,
              updated[serverIndex].tools,
            );
            return updated;
          });
        } else {
          const errorText = await response.text();
          console.error(
            `Failed to load tools for server ${serverId}: ${response.status} ${response.statusText}`,
            errorText,
          );

          // Mark server as not loading but without tools
          setMcpServers((prev) => {
            const updated = [...prev];
            updated[serverIndex] = { ...updated[serverIndex], isLoading: false };
            return updated;
          });
        }
      } else if (server.url) {
        // Use the existing live-tools API for URL-based servers
        const liveToolsUrl = buildApiUrl(`/api/mcp/live-tools?url=${encodeURIComponent(server.url)}`);
        console.log(`Making request to: ${liveToolsUrl}`);
        const response = await fetch(liveToolsUrl);

        if (response.ok) {
          const data = await response.json();
          console.log(`Received tools data:`, data);

          const toolsList = data.tools || [];
          const processedTools = toolsList.map(
            (tool: any) =>
              ({
                ...tool, // Keep ALL original properties
                id: `${serverId}_${tool.name}`,
                mcpServerName: server.name,
                serverId: serverId,
                serverUrl: server.url,
              }) as MCPToolExtended,
          );

          // Update cache
          setMcpToolsCache(prev => new Map(prev.set(serverId, processedTools)));
          setLastFetchTime(prev => new Map(prev.set(serverId, Date.now())));

          // Update the server with its tools
          setMcpServers((prev) => {
            console.log(
              `Updating server ${serverId} with ${toolsList.length} tools`,
            );

            const updated = [...prev];
            updated[serverIndex] = {
              ...updated[serverIndex],
              tools: processedTools,
              isLoading: false,
            };

            console.log(
              `Server ${serverId} now has tools:`,
              updated[serverIndex].tools,
            );
            return updated;
          });
        } else {
          const errorText = await response.text();
          console.error(
            `Failed to load tools for server ${serverId}: ${response.status} ${response.statusText}`,
            errorText,
          );

          // Mark server as not loading but without tools
          setMcpServers((prev) => {
            const updated = [...prev];
            updated[serverIndex] = { ...updated[serverIndex], isLoading: false };
            return updated;
          });
        }
      } else {
        console.error(`Server ${serverId} has no URL or config`);
        return;
      }
    } catch (error) {
      console.error(`Error loading tools for server ${serverId}:`, error);

      // Mark server as not loading
      setMcpServers((prev) => {
        const updated = [...prev];
        if (serverIndex !== -1) {
          updated[serverIndex] = { ...updated[serverIndex], isLoading: false };
        }
        return updated;
      });
    }
  };

  // Function to refresh tools for all servers
  const refreshAllTools = async () => {
    for (const server of mcpServers) {
      fetchToolsForServer(server.id, true); // Force refresh
    }
  };

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (onChange) {
      onChange(e);
    } else {
      setLocalValue(e.target.value);
    }
    adjustHeight();
  }, [onChange, adjustHeight]);

  const handleSubmit = useCallback((e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (loading) return;

    // Don't submit empty messages
    if (!inputValue.trim()) return;

    if (onSubmit) {
      // Pass knowledge sources if any are selected
      const formData = new FormData(e.currentTarget);
      const enrichedEvent = {
        ...e,
        currentTarget: {
          ...e.currentTarget,
          formData: {
            ...formData,
            knowledgeSources:
              selectedKnowledgeSources.length > 0
                ? selectedKnowledgeSources
                : undefined,
          },
        },
      } as FormEvent<HTMLFormElement>;

      onSubmit(enrichedEvent);
    }

    // Reset local value for uncontrolled usage
    if (value === undefined) {
      setLocalValue("");
      // Reset height
      adjustHeight(true);
    }
  }, [loading, inputValue, onSubmit, selectedKnowledgeSources, value, adjustHeight]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      console.log("VercelV0Chat: Enter key pressed without shift");
      e.preventDefault();
      if (inputValue.trim()) {
        console.log(
          "VercelV0Chat: Input has value, handling submission directly",
        );
        // Create a synthetic form event and pass it to handleSubmit
        const syntheticEvent = {
          preventDefault: () => {},
          target: e.currentTarget.form,
        } as unknown as FormEvent<HTMLFormElement>;

        handleSubmit(syntheticEvent);
      }
    }
  }, [inputValue, handleSubmit]);

  const handleToolSelect = useCallback((tool: MCPToolExtended | null) => {
    if (onMCPToolSelect) {
      onMCPToolSelect(tool);
    }
  }, [onMCPToolSelect]);

  const toggleToolSelection = useCallback((toolId: string) => {
    const allTools = mcpServers.flatMap((server: any) => server.tools);
    const tool = allTools.find((t: any) => t.id === toolId);
    
    if (tool) {
      setSelectedMCPTools((prev: any) => {
        const isSelected = prev.includes(toolId);
        if (isSelected) {
          // Remove from selection
          const newSelection = prev.filter((id: any) => id !== toolId);
          // Update the callback with the new selection
          if (onMCPToolSelect) {
            if (newSelection.length === 0) {
              onMCPToolSelect(null);
            } else {
              const selectedTools = allTools.filter((t: any) => newSelection.includes(t.id));
              onMCPToolSelect(selectedTools[0]); // Pass first tool as primary
            }
          }
          return newSelection;
        } else {
          // Add to selection
          const newSelection = [...prev, toolId];
          // Update the callback with the new selection
          if (onMCPToolSelect) {
            const selectedTools = allTools.filter((t: any) => newSelection.includes(t.id));
            onMCPToolSelect(selectedTools[0]); // Pass first tool as primary
          }
          return newSelection;
        }
      });
    }
  }, [mcpServers, onMCPToolSelect]);

  const toggleServerCollapse = (serverId: string) => {
    setCollapsedServers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(serverId)) {
        newSet.delete(serverId);
      } else {
        newSet.add(serverId);
      }
      return newSet;
    });
  };

  const clearAllMCPSelections = useCallback(() => {
    setSelectedMCPTools([]);
    if (onMCPToolSelect) {
      onMCPToolSelect(null);
    }
  }, [onMCPToolSelect]);

  // Toggle knowledge source selection
  const toggleKnowledgeSourceSelection = useCallback((sourceId: string) => {
    const newSelectedSources = selectedKnowledgeSources.includes(sourceId)
      ? selectedKnowledgeSources.filter((id) => id !== sourceId)
      : [...selectedKnowledgeSources, sourceId];

    if (onKnowledgeSourcesSelect) {
      onKnowledgeSourcesSelect(newSelectedSources);
    }
  }, [selectedKnowledgeSources, onKnowledgeSourcesSelect]);

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  // Function to refresh knowledge sources
  const refreshKnowledgeSources = useCallback(async () => {
    try {
      setIsLoadingKnowledge(true);
      
      // Build URL with parameters
      const params = new URLSearchParams();
      if (organizationId) {
        params.append('organizationId', organizationId);
      }
      
      const url = buildApiUrl(`/api/knowledge/user-accessible${params.toString() ? `?${params.toString()}` : ''}`);
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        // Filter only completed sources
        const completedSources = data.sources.filter(
          (source: KnowledgeSource) => source.status === "COMPLETED",
        );
        setKnowledgeSources(completedSources || []);
        // Set the scopes and admin status from the API response
        setKnowledgeScopes(data.scopes || []);
        setIsAdmin(data.isAdmin || false);
      } else {
        console.error(
          "Failed to fetch knowledge sources:",
          await response.text(),
        );
      }
    } catch (error) {
      console.error("Error fetching knowledge sources:", error);
    } finally {
      setIsLoadingKnowledge(false);
    }
  }, [organizationId]);

  return (
    <div className="w-full max-w-4xl mx-auto">
      <form
        onSubmit={handleSubmit}
        className="relative flex w-full flex-col backdrop-blur-md bg-background/20 border border-border/40 rounded-lg before:absolute before:inset-0 before:bg-gradient-to-r before:from-black/5 before:via-transparent before:to-black/5 before:pointer-events-none before:rounded-md before:z-0 overflow-hidden"
      >
        <div className={cn(
          "flex-1 ml-2 items-end gap-2 sm:gap-4 mb-4 bg-transparent relative z-10 px-2 sm:px-0",
          dropdownClassName
        )}>
          {/* Knowledge sources dropdown */}
          <DropdownMenu
            open={knowledgeDropdownOpen}
            onOpenChange={setKnowledgeDropdownOpen}
          >
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-6 px-2 sm:px-3 border border-border rounded-lg text-sm hover:bg-muted/50 transition-all duration-200 group"
              >
                <BoxIcon className="w-3.5 h-3.5 mr-1 sm:mr-2 text-muted-foreground group-hover:text-foreground transition-colors" />
                {selectedKnowledgeSources.length > 0 ? (
                  <div className="flex items-center gap-1 sm:gap-2">
                    <span className="font-medium">
                      {selectedKnowledgeSources.length}
                    </span>
                    <span className="text-muted-foreground hidden sm:inline">Selected</span>
                  </div>
                ) : (
                  <TextShimmer
                    duration={10}
                    className="text-foreground/40 text-xs"
                  >
                    Knowledge Sources
                  </TextShimmer>
                )}
                <ChevronDown className="w-3.5 h-3.5 ml-1 sm:ml-2 text-muted-foreground group-hover:text-foreground transition-all duration-200 group-data-[state=open]:rotate-180" />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              className="w-80 p-0 border border-accent/20 shadow-lg"
              align="start"
              alignOffset={4}
              side={dropdownDirection}
              sideOffset={5}
              style={{ zIndex: 999999 }}
              avoidCollisions={true}
              collisionPadding={8}
            >
              {/* Header */}
              <div className="p-4 border-b border-border/30">
                {isLoadingKnowledge ? (
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="w-4 h-4 border border-muted rounded-full"></div>
                      <div className="w-4 h-4 border border-foreground border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      Loading sources...
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium">Knowledge Sources</h4>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {selectedKnowledgeSources.length > 0
                          ? `${selectedKnowledgeSources.length} of ${knowledgeSources.length} selected`
                          : `${knowledgeSources.length} available`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      {selectedKnowledgeSources.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                          onClick={(e) => {
                            e.preventDefault();
                            if (onKnowledgeSourcesSelect) {
                              onKnowledgeSourcesSelect([]);
                            }
                          }}
                        >
                          Clear
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                        onClick={(e) => {
                          e.preventDefault();
                          refreshKnowledgeSources();
                        }}
                      >
                        <RefreshCw className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-2">
                {knowledgeSources.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="w-12 h-12 bg-muted/50 rounded-xl flex items-center justify-center mb-3">
                      <FileText className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      No sources available
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Upload documents to get started
                    </p>
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto space-y-1">
                    {knowledgeSources.map((source, index) => (
                      <div
                        key={source.id}
                        className="group flex items-center gap-3 p-3 rounded-lg hover:bg-muted/30 transition-all duration-200 cursor-pointer animate-in fade-in slide-in-from-bottom-1"
                        style={{ animationDelay: `${index * 30}ms` }}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleKnowledgeSourceSelection(source.id);
                        }}
                      >
                        {/* Checkbox */}
                        <div className="relative">
                          <div
                            className={cn(
                              "w-4 h-4 rounded border-2 transition-all duration-200 flex items-center justify-center",
                              selectedKnowledgeSources.includes(source.id)
                                ? "bg-foreground border-foreground"
                                : "border-border group-hover:border-muted-foreground",
                            )}
                          >
                            {selectedKnowledgeSources.includes(source.id) && (
                              <svg
                                className="w-2.5 h-2.5 text-background"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            )}
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h5 className="text-sm font-medium truncate">
                              {source.name}
                            </h5>
                            {source.status === "COMPLETED" && (
                              <div className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0" />
                            )}
                            {source.status === "PROCESSING" && (
                              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse flex-shrink-0" />
                            )}
                            {source.status === "FAILED" && (
                              <div className="w-1.5 h-1.5 bg-red-500 rounded-full flex-shrink-0" />
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span className="px-1.5 py-0.5 bg-muted/50 rounded text-xs font-medium">
                              {source.file_type.split("/")[1]?.toUpperCase() ||
                                "FILE"}
                            </span>
                            <span>•</span>
                            <span>{formatFileSize(source.file_size)}</span>
                            <span>•</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              {knowledgeSources.length > 0 && (
                <div className="p-3 border-t border-border/30 bg-muted/20">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Click to select sources</span>
                    {selectedKnowledgeSources.length > 0 && (
                      <span className="font-medium">
                        {selectedKnowledgeSources.length} selected
                      </span>
                    )}
                  </div>
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* MCP Tool Selector - always show it */}
          <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-6 px-2 sm:px-3 border border-border rounded-lg text-sm hover:bg-accent/20 transition-all duration-200 group"
              >
                <RectangleHorizontalIcon  className="w-3.5 h-3.5 mr-1 sm:mr-2 text-muted-foreground group-hover:text-foreground transition-colors" 
                />
                {selectedMCPTools.length > 0 ? (
                  <div className="flex items-center gap-1 sm:gap-2">
                    <span className="font-medium">{selectedMCPTools.length}</span>
                    <span className="text-muted-foreground hidden sm:inline">Tools</span>
                  </div>
                ) : (
                   <TextShimmer
                    duration={10}
                    className="text-foreground/40 text-xs"
                  >
                    MCP Tools
                  </TextShimmer>
                )}
                <ChevronDown className="w-3 h-3 ml-1 sm:ml-2 text-muted-foreground group-hover:text-foreground transition-colors" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-80 sm:w-96 max-h-[70vh] overflow-hidden"
              side={dropdownDirection}
              sideOffset={5}
              style={{ zIndex: 999999 }}
              avoidCollisions={true}
              collisionPadding={8}
            >
              <DropdownMenuLabel className="flex items-center justify-between text-xs sticky top-0 z-10 bg-background">
                <span>
                  MCP Tools{" "}
                  {selectedMCPTools.length > 0 && `(${selectedMCPTools.length} selected)`}
                </span>
                <div className="flex items-center gap-1">
                  {selectedMCPTools.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 px-1 text-xs"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        clearAllMCPSelections();
                      }}
                    >
                      Clear All
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      refreshAllTools();
                    }}
                  >
                    <RefreshCw className="h-3 w-3" />
                  </Button>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {isLoadingServers ? (
                <DropdownMenuItem disabled>
                  <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                  <span className="text-xs">Loading servers...</span>
                </DropdownMenuItem>
              ) : (
                <div className="max-h-[400px] overflow-y-auto">
                  {/* Group tools by server */}
                  {mcpServers.length > 0 ? (
                    mcpServers.map((server) => (
                      <DropdownMenuGroup key={server.id}>
                        <DropdownMenuLabel 
                          className="text-xs font-medium px-3 py-2 bg-muted/30 flex items-center justify-between sticky top-0 cursor-pointer hover:bg-muted/50"
                          onClick={() => toggleServerCollapse(server.id)}
                        >
                          <div className="flex items-center gap-2">
                            <ChevronDown 
                              className={`w-3 h-3 text-muted-foreground transition-transform ${
                                collapsedServers.has(server.id) ? '-rotate-90' : ''
                              }`}
                            />
                            <RectangleHorizontalIcon 
                              className="w-3 h-3 text-muted-foreground" 
                            />
                            <span className="font-medium">{server.name}</span>
                            {server.tools.length > 0 && (
                              <span className="text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
                                {server.tools.length} tools
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {server.isLoading && (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0 text-muted-foreground hover:text-foreground"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                fetchToolsForServer(server.id, true);
                              }}
                            >
                              <RefreshCw className="h-2.5 w-2.5" />
                            </Button>
                          </div>
                        </DropdownMenuLabel>

                        {!collapsedServers.has(server.id) && (
                          <>
                            {server.tools.length === 0 && !server.isLoading ? (
                              <DropdownMenuItem
                                disabled
                                className="text-xs opacity-70 py-2 px-3"
                              >
                                <div className="flex items-center gap-2 text-muted-foreground">
                                  <div className="w-1 h-1 bg-muted-foreground/50 rounded-full"></div>
                                  No tools available
                                </div>
                              </DropdownMenuItem>
                            ) : (
                              <div className="space-y-0.5 py-1">
                                {server.tools.map((tool) => (
                                  <DropdownMenuItem
                                    key={tool.id}
                                    className="text-xs py-3 px-3 relative group cursor-pointer hover:bg-accent/20"
                                    onSelect={(e) => {
                                      e.preventDefault();
                                      toggleToolSelection(tool.id);
                                    }}
                                  >
                                    <div className="flex items-start gap-3 w-full">
                                      <div className="flex-shrink-0 mt-0.5">
                                        <div
                                          className={cn(
                                            "w-3 h-3 border rounded flex items-center justify-center transition-all",
                                            selectedMCPTools.includes(tool.id)
                                              ? "bg-primary border-primary"
                                              : "border-border hover:border-muted-foreground"
                                          )}
                                        >
                                          {selectedMCPTools.includes(tool.id) && (
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeWidth="3"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              className="w-2 h-2 text-primary-foreground"
                                            >
                                              <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span
                                            className={cn(
                                              "font-medium text-sm",
                                              selectedMCPTools.includes(tool.id)
                                                ? "text-foreground"
                                                : "text-foreground/90"
                                            )}
                                          >
                                            {formatToolName(tool.name)}
                                          </span>
                                        </div>
                                        <div className="text-[10px] text-muted-foreground mb-1">
                                          Tool: {tool.name}
                                        </div>
                                        {tool.description && (
                                          <p title={tool.description} className="text-[10px] text-muted-foreground/80 leading-relaxed whitespace-pre-wrap line-clamp-2 truncate">
                                            {tool.description}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </DropdownMenuItem>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </DropdownMenuGroup>
                    ))
                  ) : (
                    <DropdownMenuItem disabled className="text-xs py-2">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <div className="w-1 h-1 bg-muted-foreground/50 rounded-full"></div>
                        No MCP servers available
                      </div>
                    </DropdownMenuItem>
                  )}
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Render extra button if provided */}
          {/* {renderExtraButton && renderExtraButton()} */}
        </div>

        {/* Main input */}
        <div className="relative flex-1 px-1 sm:px-2 py-2 pb-4">
          <Textarea
            ref={textareaRef}
            tabIndex={0}
            placeholder={placeholder}
            className={cn(
              "min-h-[115px] w-full resize-none bg-background pl-3 sm:pl-4 pr-3 py-3 pb-12 text-sm rounded-lg border border-input focus-visible:ring-[1px] focus-visible:ring-offset-0 focus-visible:ring-ring/50",
              inputClassName
            )}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />

          {/* Add buttons positioned relative to input container */}
          <div className={cn(
            "absolute bottom-4 right-3 flex items-center gap-1.5 z-10",
            buttonsClassName
          )}>
            {/* Suggestions toggle button */}
            {setShowSuggestions && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-6 px-2 text-[10px] bg-background/95 backdrop-blur-sm border-border/50 hover:bg-muted/70 transition-all duration-200"
                onClick={() => setShowSuggestions(!showSuggestions)}
              >
                <MonitorIcon className="w-3 h-3 mr-1" />
                {showSuggestions ? "Hide" : "Show"}
              </Button>
            )}

            {/* Intermediate steps toggle button */}
            {showIntermediateStepsToggle && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-6 px-2 text-[10px] bg-background/95 backdrop-blur-sm border-border/50 hover:bg-muted/70 transition-all duration-200"
                onClick={() => setIntermediateSteps?.(!intermediateSteps)}
              >
                <PlusIcon className="w-3 h-3 mr-1" />
                {intermediateSteps ? "Hide" : "Steps"}
              </Button>
            )}

            {/* Send button */}
            <Button
              size="sm"
              className={cn(
                "h-6 w-7 rounded-md bg-primary text-primary-foreground px-0",
                (loading || !inputValue.trim()) &&
                  "opacity-50 cursor-not-allowed",
              )}
              disabled={loading || !inputValue.trim()}
              type="submit"
              onClick={(e) => {
                e.preventDefault();
                if (!loading && inputValue.trim()) {
                  // Create a synthetic form event
                  const form = e.currentTarget.closest('form');
                  if (form) {
                    const syntheticEvent = {
                      preventDefault: () => {},
                      currentTarget: form,
                    } as FormEvent<HTMLFormElement>;
                    handleSubmit(syntheticEvent);
                  }
                }
              }}
            >
              {loading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <ArrowUpIcon className="h-3 w-3" />
              )}
            </Button>
          </div>
        </div>
      </form>

      {/* Display selected knowledge sources */}
      {selectedKnowledgeSources.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selectedKnowledgeSources.map((sourceId) => {
            const source = knowledgeSources.find((s) => s.id === sourceId);
            return (
              source && (
                <Badge
                  key={sourceId}
                  variant="outline"
                  className="bg-primary/5 border-primary/20 text-xs font-normal py-0 h-5"
                >
                  <FileText className="h-3 w-3 mr-1" />
                  {source.name}
                  <button
                    type="button"
                    className="ml-1 rounded-full hover:bg-muted p-0.5"
                    onClick={() => toggleKnowledgeSourceSelection(sourceId)}
                  >
                    <X className="h-2 w-2" />
                  </button>
                </Badge>
              )
            );
          })}
        </div>
      )}

      {/* Keep existing toggles and options */}
    </div>
  );
}

interface ActionButtonProps {
  icon: React.ReactNode;
  label: string;
}

function ActionButton({ icon, label }: ActionButtonProps) {
  return (
    <button
      type="button"
      className="flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent/80 rounded-full border border-border text-muted-foreground hover:text-foreground transition-colors"
    >
      {icon}
      <span className="text-xs">{label}</span>
    </button>
  );
}
